import { useCallback, useEffect, useState } from "react";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import DocumentTable from "../components/DocumentTable";
import DocumentForm from "../components/DocumentForm";
import DocumentDetailView from "../components/DocumentDetailView";
import { DocumentMaster } from "../../../services/documentMasterService";
import documentMasterService from "../../../services/documentMasterService";

const ITEMS_PER_PAGE = 6;

export default function Documents() {
  const [documents, setDocuments] = useState<DocumentMaster[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingDocument, setEditingDocument] = useState<DocumentMaster | null>(null);
  const [viewingDocument, setViewingDocument] = useState<DocumentMaster | null>(null);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<"Active" | "Inactive" | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        page: number;
        limit: number;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
        search?: string;
        status?: "Active" | "Inactive";
      } = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      if (search) params.search = search;
      if (status) params.status = status;

      const result = await documentMasterService.getDocumentMasters(params);
      setDocuments(result.data || []);
      setTotalDocuments(result.meta?.totalDocs || 0);
    } catch (err: any) {
      console.error("Failed to fetch documents", err);
      setError(err.message || "Failed to fetch documents");
      setDocuments([]);
      setTotalDocuments(0);
    } finally {
      setLoading(false);
    }
  }, [search, status, page, sortField, sortOrder]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleAdd = async (documentData: {
    documentName: string;
    documentDescription?: string;
    status?: 'Active' | 'Inactive';
  }) => {
    try {
      const documentId = editingDocument?._id || editingDocument?.id;
      if (editingDocument && documentId) {
        // Update existing document
        await documentMasterService.updateDocumentMaster(documentId, documentData);
      } else {
        // Create new document
        await documentMasterService.createDocumentMaster(documentData);
      }
      setEditingDocument(null);
      setOpenForm(false);
      // Refetch data from backend to ensure consistency
      fetchDocuments();
    } catch (error: any) {
      console.error("Failed to save document:", error);
      setError(error.message || "Failed to save document");
      throw error; // Re-throw to let DocumentForm handle the error display
    }
  };

  const handleView = (document: DocumentMaster) => {
    setViewingDocument(document);
    setShowDetailView(true);
  };

  const handleEdit = (document: DocumentMaster) => {
    setEditingDocument(document);
    setOpenForm(true);
  };

  const handleToggleStatus = async (document: DocumentMaster) => {
    try {
      const documentId = document._id || document.id;
      if (!documentId) {
        setError("Document ID is missing");
        return;
      }
      if (document.status === 'Active') {
        await documentMasterService.inactivateDocumentMaster(documentId);
      } else {
        await documentMasterService.activateDocumentMaster(documentId);
      }
      // Refresh to get updated data
      fetchDocuments();
    } catch (error: any) {
      console.error("Failed to toggle document status:", error);
      setError(error.message || "Failed to toggle document status");
    }
  };

  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      {/* Page Header */}
      <PageBreadcrumb pageTitle="Document Management" />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Document Table */}
      <DocumentTable
        documents={documents}
        search={search}
        setSearch={setSearch}
        status={status}
        setStatus={(val: string | null) => setStatus(val as any)}
        page={page}
        setPage={setPage}
        totalDocuments={totalDocuments}
        onEdit={handleEdit}
        onView={handleView}
        onToggleStatus={handleToggleStatus}
        onAdd={() => {
          setEditingDocument(null);
          setOpenForm(true);
        }}
        onRefresh={fetchDocuments}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        loading={loading}
      />

      {/* Document Form Drawer */}
      <RightDrawer
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditingDocument(null);
        }}
        size="60"
      >
        <div className="p-1">
          <DocumentForm
            editingDocument={editingDocument}
            onAdd={handleAdd}
            onClose={() => {
              setOpenForm(false);
              setEditingDocument(null);
            }}
          />
        </div>
      </RightDrawer>

      {/* Document Detail View Drawer */}
      <RightDrawer
        open={showDetailView}
        title={viewingDocument ? viewingDocument.documentName : 'Document details'}
        onClose={() => {
          setShowDetailView(false);
          setViewingDocument(null);
        }}
        size="xl"
      >
        <DocumentDetailView document={viewingDocument} />
      </RightDrawer>
    </div>
  );
}
